function parseIntOr<T>(value: string, defaultResult: T): number | T {
  const parsed = parseInt(value, 10);
  if (isNaN(parsed)) {
    return defaultResult;
  }
  return parsed;
}

function parseFloatOr<T>(value: string, defaultResult: T): number | T {
  const parsed = parseFloat(value);
  if (isNaN(parsed)) {
    return defaultResult;
  }
  return parsed;
}

function parseIntOrUndefined(value: string): number | undefined {
  return parseIntOr(value, undefined);
}

function parseFloatOrUndefined(value: string): number | undefined {
  return parseFloatOr(value, undefined);
}

function parseIntOrZero(value: string): number {
  return parseIntOr(value, 0);
}

function parseFloatOrZero(value: string): number {
  return parseFloatOr(value, 0);
}

function parseIntOrNull(value: string | null): number | null {
  if (value === null) {
    return null;
  }
  return parseIntOr(value, null);
}

function parseFloatOrNull(value: string | null): number | null {
  if (value === null) {
    return null;
  }
  return parseFloatOr(value, null);
}

function numberOfDigit(value: number): number {
  if (!isFinite(value)) {
    return 0;
  }

  let e = 1;
  let p = 0;

  while (Math.round(value * e) / e !== value) {
    e *= 10;
    p += 1;
  }
  return p;
}

const isBetween = (inferior: number | undefined, value: number, superior: number | undefined) =>
  (inferior ?? -Infinity) <= value && value <= (superior ?? Infinity);

const padNumberToDisplay = (value: number): string => {
  if (value < 0 && value > -10) {
    return `-0${Math.abs(value)}`;
  }
  if (value >= 0 && value < 10) {
    return `0${value}`;
  }
  return value.toString();
};

const formatTimer = (timer: number): string =>
  `${padNumberToDisplay(Math.floor(timer / 60))}:${padNumberToDisplay(Math.abs(timer) % 60)}`;

const isNumber = (number: number | null | undefined): number is number =>
  number !== null && number !== undefined && !isNaN(number);

const numberToString = (value: number | string, float = 1) => {
  if (typeof value === 'string') return value;
  return value.toFixed(float);
};

const round = (value: number, decimals: number): number =>
  Number(`${Math.round(Number(`${value}e${decimals}`))}e-${decimals}`);

const roundForDisplay = (value: number, decimals: number): string => value.toFixed(decimals);

const adjust = (value: number, fromMin: number, fromMax: number, toMin: number, toMax: number) =>
  round(toMin + ((toMax - toMin) * (value - fromMin)) / (fromMax - fromMin), 0);

const displayThousands = (
  value: number | undefined,
  kDecimals = 1,
  decimals = 0,
  showSign = false
) => {
  if (value === undefined) {
    return '-';
  }
  const sign = showSign && value > 0 ? '+' : '';
  if (Math.abs(value) < 1000) {
    return `${sign}${round(value, decimals).toString()}`;
  }
  return `${sign}${round(value / 1000, kDecimals)}k`;
};

const isTypeNumber = (value: unknown): value is number => typeof value === 'number';

export {
  parseIntOrUndefined,
  parseFloatOrUndefined,
  parseIntOrZero,
  parseFloatOrZero,
  parseIntOrNull,
  parseFloatOrNull,
  numberOfDigit,
  isBetween,
  padNumberToDisplay,
  formatTimer,
  isNumber,
  numberToString,
  round,
  roundForDisplay,
  adjust,
  displayThousands,
  isTypeNumber
};
